import React, { useMemo } from 'react';
import type { MarketingCampaign } from '../../../api/services/marketingService';
import { ICONS, LineIcon, StatusPill } from '../../../components/ui/dg';
import { ago, audienceText, campaignState, channelsText, formatDateTime } from './shared';

/**
 * Liste des campagnes, à gauche : celles qui attendent une décision d'abord
 * (à approuver, puis prêtes à partir), ensuite les envoyées et les refusées,
 * les plus récentes en haut.
 */

const ORDER: Record<string, number> = { pending: 0, approved: 1, sending: 1, sent: 2, rejected: 3 };

const when = (c: MarketingCampaign) =>
  c.status === 'sent' ? `Le ${formatDateTime(c.sent_at)}`
    : c.status === 'approved' ? 'Prête à partir'
    : c.status === 'rejected' ? `Refusée ${ago(c.reviewed_at || c.updated_at)}`
    : `Ouverte ${ago(c.created_at)}`;

interface Props {
  campaigns: MarketingCampaign[];
  loading: boolean;
  error: string | null;
  selectedId: string | null;
  onRetry: () => void;
  onSelect: (campaign: MarketingCampaign) => void;
}

const CampaignList = ({ campaigns, loading, error, selectedId, onRetry, onSelect }: Props) => {
  const rows = useMemo(() => [...campaigns].sort((a, b) =>
    (ORDER[a.status] ?? 9) - (ORDER[b.status] ?? 9)
    || new Date(b.sent_at || b.updated_at).getTime() - new Date(a.sent_at || a.updated_at).getTime()), [campaigns]);

  if (loading && campaigns.length === 0) {
    return (
      <div className="space-y-2 px-3">
        {[0, 1, 2, 3].map((i) => <div key={i} className="h-[76px] animate-pulse rounded-2xl bg-[#F3F4F8]" />)}
      </div>
    );
  }
  if (error) {
    return (
      <div className="px-6 py-10 text-center text-[13px] text-[#B91C1C]">
        {error}
        <button type="button" onClick={onRetry} className="mt-2 block w-full text-[12.5px] font-bold text-[#4F46E5]">Réessayer</button>
      </div>
    );
  }
  if (rows.length === 0) return <p className="px-6 py-12 text-center text-[13px] text-[#6B7280]">Aucune campagne pour le moment.</p>;

  return (
    <ul className="space-y-1 px-3 pb-3">
      {rows.map((c) => {
        const state = campaignState(c.status);
        const active = c.id_campaign === selectedId;
        const waiting = c.status === 'pending';
        return (
          <li key={c.id_campaign}>
            <button type="button" onClick={() => onSelect(c)}
              className={`flex w-full items-center gap-3.5 rounded-2xl border-2 px-4 py-3.5 text-left transition-colors ${active ? 'border-[#C7C4F6] bg-[#F5F4FE]' : 'border-transparent hover:bg-[#F7F7FB]'}`}>
              <span className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full ${waiting ? 'bg-[#FFF1DC]' : 'bg-[#EFEEFD]'}`}>
                <LineIcon d={ICONS.megaphone} size={18} color={waiting ? '#D97706' : '#4F46E5'} />
              </span>
              <span className="min-w-0 flex-1">
                <b className="block truncate text-[15px] font-bold text-[#1E293B]">{c.content?.fr?.title || '(sans titre)'}</b>
                <span className="block truncate text-[13px] text-[#6B7280]">{channelsText(c)} · {audienceText(c)} · {when(c)}</span>
              </span>
              <StatusPill tone={state.tone}>{state.label}</StatusPill>
            </button>
          </li>
        );
      })}
    </ul>
  );
};

export default CampaignList;
